import { CloseIcon } from '../../../../assets/icon/svg'
import { useDispatch, useSelector } from 'react-redux';
import { selectSelectedCategory, selectSelectedTabs, selectTypeOfModal, setCategory } from '../../../../features/it-user-forms/user-forms-slice';
import { statusOptions } from '../../../../data/system';
import { useCategoryActions } from '../../../../hooks/actions/use-category-actions';
import { useMutation } from '../../../../hooks/services/mutations/use-mutation';
import Label from '../../../ui/label';
import Input from '../../../ui/input';
import { customStyle, defaultStyle } from '../../../../constant/custom-style';

export default function CategoryModal() {

    const dispatch = useDispatch()
    const category = useSelector(selectSelectedCategory);
    const typeOfCategory = useSelector(selectSelectedTabs);
    const typeOfAction = useSelector(selectTypeOfModal);
    const isUpdate = typeOfAction === 'update'
    const title = typeOfCategory.slice(0, -1)
    const btnTitle = isUpdate ? `Save update ${title}` : `Save ${title}`
    const buttonCreateClass = "disabled:cursor-not-allowed flex item-center justify-center space-x-1 rounded bg-[#186cb2] px-3 py-2 text-xs font-medium text-white transition";
    const { handleSaveCategory } = useCategoryActions()
    const { handleClose } = useCategoryActions()
    const { isLoadingCreateCategory, isLoadingUpdateCategory, } = useMutation()
    const disabled = isLoadingCreateCategory || isLoadingUpdateCategory

    const modalTitle = isUpdate
        ? `Update ${title}`
        : `Create ${title}`

    const modalSubtitle = isUpdate
        ? `Update the ${title} information below.`
        : `Enter the ${title} information below.`

    const buttonTitle = isUpdate
        ? `Save Changes`
        : `Create ${title}`

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-[1px]">
            <div className="relative flex w-full max-w-md flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                    <div className="flex min-w-0 items-center gap-3">
                        {/* Header Icon */}
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#186cb2]/10 text-[#186cb2]">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 3h6m-7 0h8a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Zm2 5h4m-4 4h6m-6 4h3" />
                            </svg>
                        </div>
                        <div className="min-w-0">
                            <h2 className="truncate text-sm font-semibold text-gray-900">
                                {modalTitle}
                            </h2>
                            <p className="mt-0.5 truncate text-xs text-gray-500">
                                {modalSubtitle}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={disabled}
                        aria-label="Close modal"
                        className="ml-3 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <CloseIcon />
                    </button>
                </div>
                {/* Body */}
                <div className="space-y-4 px-4 py-4">
                    {/* Name */}
                    <div>
                        <Label
                            label="Name"
                            fontSize="text-xs mb-1"
                            requiredText=" *"
                            isRequired={true}
                        />
                        <Input
                            type="text"
                            name="name"
                            value={category.name}
                            isCustomStyle={true}
                            customCss={`${defaultStyle} ${!category.name ? 'italic font-medium' : customStyle}`}
                            onChange={(e) => dispatch(setCategory({
                                type: 'name',
                                value: e.target.value,
                            }))}
                            required={true}
                            placeholder={`Enter ${title.toLowerCase()} name`}
                            disabled={disabled}
                        />
                        <p className="mt-1 text-[10px] text-gray-400">
                            Use a clear and descriptive name.
                        </p>
                    </div>

                    {/* Code */}
                    <div>
                        <Label
                            label="Code"
                            fontSize="text-xs mb-1"
                            requiredText=" *"
                            isRequired={true}
                        />
                        <Input
                            type="text"
                            name="code"
                            value={category.code}
                            isCustomStyle={true}
                            customCss={`${defaultStyle} ${!category.code ? 'italic font-medium' : customStyle}`}
                            onChange={(e) => dispatch(setCategory({
                                type: 'code',
                                value: e.target.value,
                            }))}
                            required={true}
                            placeholder={`Enter ${title.toLowerCase()} code`}
                            disabled={disabled}
                        />
                        <p className="mt-1 text-[10px] text-gray-400">
                            Use a unique code for identification.
                        </p>
                    </div>

                    {/* Status */}
                    {isUpdate && (
                        <div>
                            <Label
                                label="Status"
                                fontSize="text-xs mb-1"
                                requiredText=" *"
                                isRequired={true}
                            />
                            <div className="grid grid-cols-2 gap-2">
                                {statusOptions.map((status) => {
                                    const isSelected = category.is_active === status.value
                                    return (
                                        <label key={status.id} className={`flex cursor-pointer items-center justify-center gap-2 rounded-md border px-3 py-2 text-xs font-medium transition-colors ${isSelected ? status.activeClass : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'}`}>
                                            <input
                                                type="radio"
                                                name="status"
                                                className="sr-only"
                                                checked={isSelected}
                                                onChange={() => dispatch(setCategory({
                                                    type: 'is_active',
                                                    value: status.value,
                                                }))}
                                                disabled={disabled}
                                            />

                                            {/* Status Indicator */}
                                            <span className={`h-1.5 w-1.5 rounded-full ${ isSelected ? 'bg-current' : 'bg-gray-300' }`} />
                                            {status.label}
                                        </label>
                                    )
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-3 border-t border-gray-100 bg-gray-50/50 px-4 py-3">
                    <div className="ml-auto flex items-center gap-2">
                        <button
                            type="button"
                            disabled={disabled}
                            onClick={handleClose}
                            className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            disabled={disabled}
                            onClick={handleSaveCategory}
                            className="flex items-center justify-center gap-1.5 rounded-md bg-[#186cb2] px-4 py-2 text-xs font-medium text-white transition-colors hover:bg-[#155b96] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {disabled ? (
                                <>
                                    <svg className="h-3.5 w-3.5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" >
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                    </svg>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-3.5 w-3.5" >
                                        <path strokeLinecap="round" strokeLinejoin="round" d="m5 12 4 4L19 6" />
                                    </svg>
                                    {buttonTitle}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}
